import React, { ReactNode } from 'react';

/**
 * Formats text by applying the following transformations:
 * 1. [(N)] -> Renders number N with violet background
 * 2. __ text __ -> Renders text with underline (ignores patterns with more than 3 consecutive underscores)
 * 
 * @param text The text to format
 * @param highlightedQuestionNumber Optional question number to highlight
 * @returns ReactNode elements with formatting applied
 */
export const formatText = (text: string, highlightedQuestionNumber?: number): ReactNode[] => {
  if (!text) return [];

  // Regex patterns
  const numberPattern = /\[\((\d+)\)\]/g;
  const underlinePattern = /__(.+?)__/g;
  
  // Find all matches for both patterns
  const matches: Array<{ pattern: RegExp, match: RegExpExecArray, type: 'number' | 'underline' }> = [];
  
  // Find number matches
  let numberMatch: RegExpExecArray | null;
  while ((numberMatch = numberPattern.exec(text)) !== null) {
    matches.push({
      pattern: numberPattern,
      match: numberMatch,
      type: 'number'
    });
  }
  
  // Find underline matches
  let underlineMatch: RegExpExecArray | null;
  while ((underlineMatch = underlinePattern.exec(text)) !== null) {
    // Check if this is a case with more than 3 consecutive underscores
    // We need to check the original text at the match position
    const matchStart = underlineMatch.index;
    const matchEnd = matchStart + underlineMatch[0].length;
    
    // Check if there are more than 3 consecutive underscores at the start or end
    let hasMoreThanThreeUnderscores = false;
    
    // Check at the start of the match
    if (matchStart > 0 && text.substring(matchStart - 1, matchStart + 2) === '___') {
      hasMoreThanThreeUnderscores = true;
    }
    
    // Check at the end of the match
    if (matchEnd < text.length && text.substring(matchEnd - 2, matchEnd + 1) === '___') {
      hasMoreThanThreeUnderscores = true;
    }
    
    // Only add to matches if it doesn't have more than 3 consecutive underscores
    if (!hasMoreThanThreeUnderscores) {
      matches.push({
        pattern: underlinePattern,
        match: underlineMatch,
        type: 'underline'
      });
    }
  }
  
  // Sort matches by their position in the text
  matches.sort((a, b) => a.match.index - b.match.index);
  
  // Build result
  const result: ReactNode[] = [];
  let lastIndex = 0;
  
  matches.forEach((item, index) => {
    const { match, type } = item;
    
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(
        <React.Fragment key={`text-${index}`}>
          {text.substring(lastIndex, match.index)}
        </React.Fragment>
      );
    }
    
    // Add the formatted match
    if (type === 'number') {
      const number = match[1]; // Extract number from [(N)]
      const isHighlighted = highlightedQuestionNumber !== undefined && 
                           parseInt(number, 10) === highlightedQuestionNumber;
      
      result.push(
        <span 
          key={`number-${index}`} 
          className={`inline-flex items-center justify-center px-1.5 py-0.5 bg-violet-500 text-white rounded-md text-xs font-medium ${isHighlighted ? 'question-number-' + number : ''}`}
          data-question-number={number}
        >
          {number}
        </span>
      );
    } else if (type === 'underline') {
      const underlinedText = match[1]; // Extract text from __ text __
      result.push(
        <span 
          key={`underline-${index}`} 
          className="underline decoration-2 decoration-gray-500"
        >
          {underlinedText}
        </span>
      );
    }
    
    lastIndex = match.index + match[0].length;
  });
  
  // Add any remaining text
  if (lastIndex < text.length) {
    result.push(
      <React.Fragment key="text-end">
        {text.substring(lastIndex)}
      </React.Fragment>
    );
  }
  
  return result;
};
